import { getCsrfToken } from "@/lib/authed-fetch";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { CalendarIcon, Upload, X, FileText, Image as ImageIcon } from "lucide-react";
import { format, addDays, isWeekend } from "date-fns";
import { cn } from "@/lib/utils";
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { PulseSequenceTags } from "@/components/pulse-sequence-tags";
import { useStatusConfigs } from "@/hooks/use-status-configs";
import { StatusBadge } from "@/components/status-badge";
import { UploadProgressDialog } from "./upload-progress-dialog";
import { logger } from "@/services/debug-logger";
import { useDebugWindow } from "@/hooks/use-debug-render";
import { formatDateForAPI } from "@/lib/date-utils";
import { detectFirstUrl } from "@/lib/pulse-link";
import {
  persistPulseDraft,
  rewriteDraftAttachmentImages,
  type DraftUploadResult,
} from "@/lib/pulse-draft-attachments";

// Hardcoded pulse status badge component for fast performance
const PulseStatusBadge = ({ statusKey }: { statusKey: string }) => {
  // Hardcoded colors matching current database values
  const pulseStatusColors: Record<string, { color: string; displayName: string }> = {
    'new_message': { color: '#8d70d3', displayName: 'New Update' },  // Lilac
    'working_on_it': { color: '#5f8ac9', displayName: 'Working on it' },  // Blue
    'stuck': { color: '#ff8358', displayName: 'Stuck' },  // Orange
    'go': { color: '#5c5c5c', displayName: 'GO' },  // Grey
    'done': { color: '#34d391', displayName: 'DONE' },  // Green
    'none': { color: '#2e3440', displayName: 'none' },  // Dark grey
    'canceled': { color: '#5c5c5c', displayName: 'Canceled' }  // Grey
  };

  const statusInfo = pulseStatusColors[statusKey] || { color: '#5f8ac9', displayName: statusKey };
  
  return (
    <span
      className="status-badge px-2 py-1 rounded text-white text-xs font-medium"
      style={{ backgroundColor: statusInfo.color }}
    >
      {statusInfo.displayName}
    </span>
  );
};

interface AddTaskDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSequences?: string[];
  initialFiles?: File[];
}

interface Attachment {
  file: File;
  url?: string;
  type: 'file' | 'image';
}

// Helper function to get next work day (excluding weekends)
function getNextWorkDay(date: Date = new Date()): Date {
  let nextDay = addDays(date, 1);
  while (isWeekend(nextDay)) {
    nextDay = addDays(nextDay, 1);
  }
  return nextDay;
}

export function AddTaskDialog({ projectId, open, onOpenChange, initialSequences = [], initialFiles }: AddTaskDialogProps) {
  const [title, setTitle] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const detectedTitleUrl = detectFirstUrl(title);
  const [description, setDescription] = useState("");
  const category = "internal";
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [selectedSequences, setSelectedSequences] = useState<string[]>(initialSequences);
  const [pulseId, setPulseId] = useState<number | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{
    open: boolean;
    files: Array<{
      name: string;
      size: number;
      progress: number;
      status: 'waiting' | 'uploading' | 'completed' | 'error';
    }>;
    totalProgress: number;
  }>({ open: false, files: [], totalProgress: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeUploadUrlsRef = useRef(new Set<string>());
  const deferredRevocationsRef = useRef(new Set<string>());
  const attachmentsRef = useRef<Attachment[]>([]);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const revokePreview = (url?: string) => {
    if (!url) return;
    if (activeUploadUrlsRef.current.has(url)) {
      deferredRevocationsRef.current.add(url);
    } else {
      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => () => {
    attachmentsRef.current.forEach(att => revokePreview(att.url));
  }, []);
  
  // Track add task dialog window state
  useDebugWindow('AddTaskDialog', open);
  
  // Get status configurations for pulses
  const { statusConfigs, getDefaultStatus, getStatusByKey, isLoading: statusesLoading } = useStatusConfigs('pulse');
  const [status, setStatus] = useState("");
  
  // Set default status to "New Update" when configs are loaded
  useEffect(() => {
    if (!statusesLoading && Array.isArray(statusConfigs) && statusConfigs.length > 0 && !status) {
      // Check if "New Update" status exists, otherwise use default
      const newUpdateStatus = statusConfigs.find((config: any) => config.statusKey === 'new_message' || config.displayName === 'New Update');
      setStatus(newUpdateStatus ? newUpdateStatus.statusKey : getDefaultStatus('pulse'));
    }
  }, [statusConfigs, statusesLoading, getDefaultStatus, status]);

  // Update selected sequences when dialog opens with initial sequences
  useEffect(() => {
    if (open && initialSequences.length > 0) {
      setSelectedSequences(initialSequences);
    }
  }, [open, initialSequences]);

  const processedDropRef = useRef(false);

  useEffect(() => {
    if (open && initialFiles && initialFiles.length > 0 && !processedDropRef.current) {
      processedDropRef.current = true;
      const newAttachments: Attachment[] = initialFiles.map(file => {
        const isImage = file.type.startsWith('image/');
        return {
          file,
          url: isImage ? URL.createObjectURL(file) : undefined,
          type: isImage ? 'image' as const : 'file' as const,
        };
      });
      attachmentsRef.current.forEach(att => revokePreview(att.url));
      setAttachments(newAttachments);

      const fileName = initialFiles[0].name;
      const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
      setTitle(nameWithoutExt);
    }
    if (!open) {
      processedDropRef.current = false;
    }
  }, [open, initialFiles]);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments: Attachment[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isImage = file.type.startsWith('image/');
      
      if (isImage) {
        // Create object URL for image preview
        const url = URL.createObjectURL(file);
        newAttachments.push({ file, url, type: 'image' });
      } else {
        newAttachments.push({ file, type: 'file' });
      }
    }
    
    setAttachments([...attachments, ...newAttachments]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Toolbar, paste, and drop images in a new pulse are staged as ordinary
  // attachments. Their object URL is preview-only and persistPulseDraft
  // replaces it after the pulse has an id and the attachment is uploaded.
  const stageDraftImage = async (file: File): Promise<string> => {
    const url = URL.createObjectURL(file);
    const attachment: Attachment = { file, url, type: 'image' };
    setAttachments(current => [...current, attachment]);
    return url;
  };

  // Remove attachment
  const removeAttachment = (index: number) => {
    const newAttachments = [...attachments];
    const removed = newAttachments.splice(index, 1)[0];
    
    // Revoke object URL to prevent memory leaks
    if (removed.url) {
      setDescription(prev => rewriteDraftAttachmentImages(
        prev,
        new Map([[removed.url!, null]]),
      ));
      revokePreview(removed.url);
    }
    
    setAttachments(newAttachments);
  };

  // Insert image into rich text editor (removed quillRef dependency)

  // Upload files to Dropbox with progress tracking
  const uploadFilesToDropbox = async (
    files: Attachment[],
    pulseId: number,
  ): Promise<DraftUploadResult<Attachment, any>> => {
    const uploadedAttachments: Array<{ draft: Attachment; uploaded: any }> = [];
    const failedAttachments: Attachment[] = [];
    
    // Show progress for all uploads
    const showProgress = files.length > 0;
    
    if (showProgress) {
      // Initialize progress tracking
      const progressFiles = files.map(f => ({
        name: f.file.name,
        size: f.file.size,
        progress: 0,
        status: 'waiting' as const
      }));
      
      setUploadProgress({
        open: true,
        files: progressFiles,
        totalProgress: 0
      });
    }
    
    let completedCount = 0;
    
    for (let i = 0; i < files.length; i++) {
      const attachment = files[i];
      if (attachment.url) activeUploadUrlsRef.current.add(attachment.url);
      const formData = new FormData();
      formData.append('file', attachment.file);
      
      try {
        // Update status to uploading
        if (showProgress) {
          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map((f, idx) => 
              idx === i ? { ...f, status: 'uploading' } : f
            )
          }));
        }
        
        const xhr = new XMLHttpRequest();
        const uploadPromise = new Promise<any>((resolve, reject) => {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && showProgress) {
              const progress = Math.round((e.loaded / e.total) * 100);
              setUploadProgress(prev => {
                const newFiles = [...prev.files];
                newFiles[i].progress = progress;
                
                // Calculate total progress
                const totalProgress = newFiles.reduce((sum, f) => sum + f.progress, 0) / newFiles.length;
                
                return {
                  ...prev,
                  files: newFiles,
                  totalProgress
                };
              });
            }
          });
          
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText));
              } catch {
                reject(new Error('Attachment upload returned an invalid response'));
              }
            } else {
              let message = `HTTP ${xhr.status}: ${xhr.statusText}`;
              try {
                message = JSON.parse(xhr.responseText)?.error || message;
              } catch {
                // Keep the HTTP status when the server did not return JSON.
              }
              reject(new Error(message));
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.open('POST', `/api/pulses/${pulseId}/attachments`);
          xhr.withCredentials = true;
          const csrfToken = getCsrfToken();
          if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken);
          xhr.send(formData);
        });
        
        const uploadedAttachment = await uploadPromise;
        uploadedAttachments.push({ draft: attachment, uploaded: uploadedAttachment });
        completedCount++;
        
        // Update status to completed
        if (showProgress) {
          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map((f, idx) => 
              idx === i ? { ...f, status: 'completed', progress: 100 } : f
            )
          }));
        }
      } catch (error) {
        failedAttachments.push(attachment);
        console.error(`Failed to upload attachment ${attachment.file.name}:`, error);
        
        // Update status to error
        if (showProgress) {
          setUploadProgress(prev => ({
            ...prev,
            files: prev.files.map((f, idx) => 
              idx === i ? { ...f, status: 'error' } : f
            )
          }));
        }
        
        toast({
          title: "Warning",
          description: `Failed to upload ${attachment.file.name}. The pulse was created without this attachment.`,
          variant: "destructive",
        });
      } finally {
        if (attachment.url) {
          activeUploadUrlsRef.current.delete(attachment.url);
          if (deferredRevocationsRef.current.delete(attachment.url)) {
            URL.revokeObjectURL(attachment.url);
          }
        }
      }
    }
    
    // Close progress dialog after a short delay
    if (showProgress) {
      setTimeout(() => {
        setUploadProgress({ open: false, files: [], totalProgress: 0 });
      }, 1000);
    }
    
    // Invalidate project files cache to refresh file browser
    queryClient.invalidateQueries({
      queryKey: [`/api/projects`],
      predicate: (query) => {
        return query.queryKey[0]?.toString().includes('/files') || false;
      }
    });
    
    return { uploadedAttachments, failedAttachments };
  };

  const createTaskMutation = useMutation({
    mutationFn: async (taskData: {
      projectId: string;
      pulseDescription: string;
      category: string;
      status: string;
      htmlContent?: string | null;
      dueDate?: string;
      files?: Attachment[];
    }) => {
      const { files, ...pulseData } = taskData;
      return persistPulseDraft({
        pulseData,
        files: files ?? [],
        createPulse: async payload => {
          const response = await apiRequest('POST', '/api/pulses', payload);
          return response.json();
        },
        uploadAttachments: uploadFilesToDropbox,
        patchHtml: async (pulseId, htmlContent) => {
          await apiRequest('PATCH', `/api/pulses/${pulseId}`, { htmlContent });
        },
      });
    },
    onSuccess: async ({ pulse: createdPulse, warnings }) => {
      logger.info('PULSE_CREATE', 'Pulse created successfully', {
        pulseId: createdPulse.id,
        projectId: createdPulse.projectId,
        title: createdPulse.title
      });
      
      // Set pulse ID for sequence assignment
      setPulseId(createdPulse.id);
      
      // Assign selected sequences to the pulse
      if (selectedSequences.length > 0) {
        for (const sequenceNumber of selectedSequences) {
          try {
            await apiRequest('POST', `/api/pulses/${createdPulse.id}/sequences`, { sequenceNumber });
          } catch (error) {
            console.error(`Failed to assign sequence ${sequenceNumber}:`, error);
          }
        }
      }
      
      // Invalidate specific project pulses query
      queryClient.invalidateQueries({ queryKey: ['/api/projects', projectId, 'pulses', 'paginated'] });
      queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
      queryClient.invalidateQueries({ queryKey: ['/api/years'] });
      // CRITICAL: Invalidate tree snapshot to update main table
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
      
      logger.debug('CACHE_INVALIDATE', 'Invalidated queries after pulse creation', {
        queries: [
          `['/api/projects', '${projectId}', 'pulses', 'paginated']`,
          '[/api/projects]',
          '[/api/years]',
          '[/api/tree/snapshot]'
        ]
      });
      
      toast({
        title: warnings.length > 0 ? "Pulse created with attachment warnings" : "Pulse created",
        description: warnings.length > 0
          ? warnings.join(' ')
          : "New pulse has been added successfully.",
        ...(warnings.length > 0 ? { variant: "destructive" as const } : {}),
      });
      handleClose();
    },
    onError: (error: any) => {
      console.error('Failed to create pulse:', error);
      toast({
        title: "Error",
        description: error?.message || "Failed to create pulse. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast({
        title: "Title required",
        description: "Please enter a title for the pulse.",
        variant: "destructive",
      });
      return;
    }

    // Extract text content from ReactQuill HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = description;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    const taskDescription = textContent.trim() ? `${title}\n\n${textContent}` : title;

    // If the title contains a URL, persist the linkUrl + optional friendly
    // linkTitle on metadata so the timeline row can render a clickable
    // external-link icon next to the title.
    const detectedUrl = detectFirstUrl(title);
    const trimmedLinkTitle = linkTitle.trim();
    const pulseMetadata: Record<string, unknown> | undefined = detectedUrl
      ? {
          linkUrl: detectedUrl,
          ...(trimmedLinkTitle ? { linkTitle: trimmedLinkTitle } : {}),
        }
      : undefined;

    const pulseData = {
      projectId,
      pulseDescription: taskDescription,
      category,
      status,
      htmlContent: description.trim() ? description : null, // Store rich HTML content
      dueDate: formatDateForAPI(dueDate),
      files: attachments, // Pass the actual files to be uploaded
      ...(pulseMetadata ? { metadata: pulseMetadata } : {}),
    };

    logger.info('PULSE_SUBMIT', 'Submitting new pulse', {
      projectId,
      title,
      category,
      status,
      hasDescription: !!description.trim(),
      hasAttachments: attachments.length > 0,
      sequenceCount: selectedSequences.length
    });
    
    createTaskMutation.mutate(pulseData);
  };

  const handleClose = () => {
    setTitle("");
    setLinkTitle("");
    setDescription("");
    const newUpdateStatus = Array.isArray(statusConfigs) ? statusConfigs.find((config: any) => config.statusKey === 'new_message' || config.displayName === 'New Update') : null;
    setStatus(newUpdateStatus ? newUpdateStatus.statusKey : getDefaultStatus('pulse'));
    setDueDate(getNextWorkDay());
    setSelectedSequences(initialSequences); // Reset to initial sequences
    setPulseId(null);
    // Clean up attachment URLs
    attachments.forEach(att => revokePreview(att.url));
    setAttachments([]);
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) onOpenChange(true);
        else handleClose();
      }}
    >
      <DialogContent className="max-w-4xl max-h-[90vh] navy-card border-gray-600 overflow-y-auto">
        <DialogHeader className="relative">
          <div className="flex justify-between items-start">
            <div>
              <DialogTitle className="text-xl text-gray-100">Add New Pulse</DialogTitle>
              <DialogDescription className="text-gray-400">
                Create a new pulse (major phase) for this project with rich text description and basic configuration.
              </DialogDescription>
            </div>
            <div className="absolute top-0 right-0 min-w-0 max-w-[200px]">
              <PulseSequenceTags
                pulseId={null}
                projectId={projectId}
                selectedSequences={selectedSequences}
                onSequenceChange={setSelectedSequences}
                isCreationMode={true}
                className="w-full"
                showInlineAllLink={true}
                labelText="Sequence Tags"
              />
            </div>
          </div>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-gray-300">
                Pulse Title <span className="text-red-400">*</span>
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter descriptive pulse title..."
                className="bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500"
                required
              />
              {detectedTitleUrl && (
                <div className="space-y-1 pt-1" data-testid="link-title-block">
                  <Label htmlFor="linkTitle" className="text-gray-300 text-xs">
                    Link Title <span className="text-gray-500">(optional — shown instead of the URL)</span>
                  </Label>
                  <Input
                    id="linkTitle"
                    value={linkTitle}
                    onChange={(e) => setLinkTitle(e.target.value)}
                    placeholder="e.g. Watch the cut"
                    className="bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-500"
                    data-testid="input-link-title"
                  />
                </div>
              )}
            </div>



            <div className="space-y-2">
              <Label htmlFor="description" className="text-gray-300">
                Detailed Description
              </Label>
              <div className="min-h-[300px] bg-gray-800 border border-gray-600 rounded-md overflow-hidden">
                <RichTextEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="Enter comprehensive description with rich text formatting..."
                  showImageButton={true}
                  allowTemporaryBlobImages={true}
                  imageUploader={stageDraftImage}
                  style={{
                    backgroundColor: '#1f2937',
                    color: '#f3f4f6',
                    border: 'none',
                    minHeight: '280px'
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Attachments</Label>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept="*/*"
              />
              
              {/* Upload button */}
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Upload className="mr-2 h-4 w-4" />
                Add Attachments
              </Button>

              {/* Attachment list */}
              {attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {attachments.map((attachment, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-800 rounded border border-gray-700"
                    >
                      <div className="flex items-center gap-2">
                        {attachment.type === 'image' ? (
                          <>
                            <ImageIcon className="h-4 w-4 text-blue-400" />
                            <span className="text-sm text-gray-300">{attachment.file.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (attachment.url) {
                                  const imageTag = `<img src="${attachment.url}" alt="${attachment.file.name}" style="max-width: 100%; height: auto;" />`;
                                  setDescription(prev => prev + imageTag);
                                }
                              }}
                              className="text-xs text-blue-400 hover:text-blue-300"
                            >
                              Insert in description
                            </Button>
                          </>
                        ) : (
                          <>
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-300">{attachment.file.name}</span>
                          </>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(index)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status" className="text-gray-300">
                  Initial Status
                </Label>
                <Select value={status} onValueChange={setStatus} disabled={statusesLoading}>
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-gray-100">
                    <SelectValue>
                      {status && !statusesLoading ? (
                        <PulseStatusBadge statusKey={status} />
                      ) : (
                        "Loading..."
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="navy-card border-gray-600">
                    {Array.isArray(statusConfigs) && statusConfigs.map((config: any) => (
                      <SelectItem key={config.id} value={config.statusKey} className="text-white">
                        <div className="flex items-center gap-2">
                          <PulseStatusBadge statusKey={config.statusKey} />
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal bg-gray-800 border-gray-600 text-gray-100 hover:bg-gray-700",
                        !dueDate && "text-gray-500"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, "PPP") : "Select due date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 navy-card border-gray-600" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                      className="bg-gray-800 text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>


          </div>

          <DialogFooter className="flex gap-3 pt-6 border-t border-gray-600">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createTaskMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
            >
              {createTaskMutation.isPending ? "Creating Pulse..." : "Create Pulse"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      
      <UploadProgressDialog
        open={uploadProgress.open}
        files={uploadProgress.files}
        totalProgress={uploadProgress.totalProgress}
      />
    </Dialog>
  );
}